import { useMutation, useQuery } from '@apollo/client';
import { isNil } from 'lodash';
import { useCallback } from 'react';
import { Mutations, Queries } from '@shared/api';
import { useAlert } from '@shared/components/Modal';
import { API } from '@shared/types/api';
import { useCurrentBusinessId } from '@shared/util/hooks';
import { i18n } from '@shared/util/i18n';
import { openLink } from '@shared/util/link';
import { findExistingSharedLink } from '../../cards/CardActionMenu/shareQrCodeOperations';

export function useUpdateChannelBundle(): (params: {
  channelId: number;
  enabled: boolean;
  includeCardData?: boolean;
}) => Promise<void> {
  const [updateChannelV2] = useMutation<
    API.UpdateChannelV2Mutation,
    API.UpdateChannelV2MutationVariables
  >(Mutations.channel.updateChannelV2);

  return useCallback(
    async ({
      channelId,
      enabled,
      includeCardData,
    }: {
      channelId: number;
      enabled: boolean;
      includeCardData?: boolean;
    }) => {
      await updateChannelV2({
        variables: {
          input: {
            id: channelId,
            bundle: { enabled, includeCardData },
          },
        },
      });
    },
    [updateChannelV2],
  );
}

interface RefetchableGraphQLResposnse<T> {
  data?: T;
  refetch: () => void;
}

export function useChannelReports(params: {
  workflowTemplateId?: string | null;
}): API.ChannelReportFragment[] | undefined {
  const { data } = useQuery<API.ChannelListReportsQuery, API.ChannelListReportsQueryVariables>(
    Queries.channel.listReports,
    {
      variables: { input: params },
      skip: isNil(params.workflowTemplateId),
    },
  );
  return data?.listReports ?? undefined;
}

export function useOpenReportLink(
  channelId: number | undefined,
): (report: API.ChannelReportFragment) => Promise<void> {
  const [createReportUrl] = useMutation<
    API.CreateReportUrlMutation,
    API.CreateReportUrlMutationVariables
  >(Mutations.channel.createReportUrl);
  const alert = useAlert();

  return useCallback(
    async (report: API.ChannelReportFragment) => {
      try {
        if (isNil(channelId)) {
          throw new Error('Channel ID is required');
        }
        const { data } = await createReportUrl({
          variables: {
            input: { id: report.id, channelId },
          },
        });
        const url = data?.createReportUrl;
        if (isNil(url)) {
          throw new Error('No report URL returned');
        }
        openLink(url);
      } catch (e) {
        console.error(e);
        alert({
          title: i18n('channel.options.reportError.title'),
          message: i18n('channel.options.reportError.message'),
        });
      }
    },
    [channelId, createReportUrl, alert],
  );
}

export function useRemoveChannel(): (id: number) => Promise<void> {
  const businessId = useCurrentBusinessId();
  const [removeChannel] = useMutation<
    API.RemoveChannelMutation,
    API.RemoveChannelMutationVariables
  >(Mutations.channel.remove);
  return useCallback(
    async (id: number) => {
      await removeChannel({
        variables: { id },
        refetchQueries: [{ query: Queries.channel.list, variables: { businessId } }],
      });
    },
    [businessId, removeChannel],
  );
}

export function useListSharedCardLinks(
  channelId: number | undefined,
  disabled: boolean = false,
): RefetchableGraphQLResposnse<API.ListSharedCardLinksQuery['listSharedCardLinks'][number][]> {
  const query = useQuery<API.ListSharedCardLinksQuery, API.ListSharedCardLinksQueryVariables>(
    Queries.channel.listSharedCardLinks,
    {
      variables: { input: { channelId: channelId! } },
      skip: disabled || isNil(channelId),
    },
  );

  return {
    data: query.data?.listSharedCardLinks,
    refetch: query.refetch,
  };
}

export function useRemoveSharedCardLink(): (id: string) => Promise<boolean> {
  const [removeSharedCardLink] = useMutation<
    API.DeleteSharedCardLinkMutation,
    API.DeleteSharedCardLinkMutationVariables
  >(Mutations.channel.removeSharedCardLink);

  return useCallback(
    async (id: string): Promise<boolean> => {
      const response = await removeSharedCardLink({ variables: { id } });
      return !!response.data?.deleteSharedCardLink;
    },
    [removeSharedCardLink],
  );
}

export type ShareCardLink = { id: string; url: string };

export function useCreateSharedLink(
  query: API.CreateSharedCardLink,
  disabled: boolean = false,
): () => Promise<ShareCardLink | undefined> {
  const { data, refetch } = useQuery<
    API.ListSharedCardLinksQuery,
    API.ListSharedCardLinksQueryVariables
  >(Queries.channel.listSharedCardLinks, {
    variables: { input: { channelId: query.channelId } },
    skip: disabled,
  });
  const [createSharedCardLink] = useMutation<
    API.CreateSharedCardLinkMutation,
    API.CreateSharedCardLinkMutationVariables
  >(Mutations.channel.createSharedCardLink);

  const links = data?.listSharedCardLinks;
  return useCallback(async (): Promise<ShareCardLink | undefined> => {
    const link = findExistingSharedLink(query, links);
    if (!isNil(link) && !isNil(link.shortUrl) && !isNil(link.id)) {
      return { id: link.id, url: link.shortUrl };
    }

    const res = await createSharedCardLink({ variables: { input: query } });
    await refetch();

    const url = res.data?.createSharedCardLink?.shortUrl;
    const id = res.data?.createSharedCardLink?.id;
    if (isNil(url) || isNil(id)) {
      return undefined;
    }

    return { url, id };
  }, [links, createSharedCardLink, refetch, query]);
}
